import React, { forwardRef, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ProductHeader from 'components/ProductPageV2/ProductHeader/ProductHeader';
import AddToCartModal, { ADD_TO_CART_STATE } from './AddToCartModal';
import ProductWrapper from './ProductWrapper';
import Tabs from './Tabs/Tabs';
import Image from 'components/Common/Medias/Image';
import InsertHtmlContent from 'components/Common/InsertHtmlContent/InsertHtmlContent';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import Loading from 'components/Common/Loading/Loading';
import Button from 'components/UI/Buttons/Button/Button';
import { cartContext } from 'contexts/cartContext';
import { apiUrls } from 'services/api-urls';
import { getCatalogSku, getRealSku, getSizeLabel } from 'services/product';
import { getUri } from 'services/generic';
import { GroupedProductPage } from 'types/ProductPage/ProductPage';
import {
  PRODUCT_TYPE,
  ProductColorsType,
  ProductKind,
  ProductType,
} from 'types/ProductPageV2/Products';
import {
  STOCK_STATUS_AVAILABLE,
  StockStatusType,
} from 'types/ProductPageV2/ProductsStocks';

import styles from './GroupedProductView.module.scss';

export type ProductState = ProductType & {
  stockState?: StockStatusType;
  qty: number;
  productType: ProductKind;
  colors: ProductColorsType[];
};

type Props = { data: GroupedProductPage };

const GroupedProductView = forwardRef(function GroupedProductView({
  data: { product, linkedProducts: linked, cmsContent, breadcrumb },
}: Props) {
  const { addToMyBasket } = useContext(cartContext);
  const { t } = useTranslation('productPage');
  const [addToCartState, setAddToCartState] = useState<ADD_TO_CART_STATE>(
    ADD_TO_CART_STATE.NONE
  );

  const [productsReady, setProductsReady] = useState<Record<number, boolean>>(
    {}
  );

  const [products, setProducts] = useState<Record<number, ProductState>>({});
  const [showLoading, setShowLoading] = useState(false);
  const linkedProducts = useMemo(() => linked.products || [], [linked]);

  const productAttributes = product.Attributes;
  const productName = productAttributes.libelle?.Value;
  const showProducts = useMemo(
    () => Object.keys(productsReady).length === linkedProducts.length,
    [productsReady, linkedProducts]
  );

  const canBuyAll = useMemo(() => {
    const values = Object.values(products);

    return (
      values.length === linkedProducts.length &&
      values.every(
        data =>
          data.productType === PRODUCT_TYPE.PRODUCT_UNIQUE &&
          data.colors.length === 1 &&
          data.stockState &&
          data.stockState === STOCK_STATUS_AVAILABLE
      )
    );
  }, [products, linkedProducts]);

  function handleBuyAll() {
    setShowLoading(true);
    const items = Object.values(products).map(product => ({
      sku: getRealSku(product.Attributes),
      catalogSku: getCatalogSku(product.Attributes),
      qty: `${product.qty}`,
      url: getUri(product.Url),
      sizeLabel: getSizeLabel(product.Attributes),
      productData: { ...product.Attributes },
    }));

    addToMyBasket(items, undefined, undefined, undefined, state => {
      setShowLoading(false);
      setAddToCartState(
        state === 'addToMyBasket'
          ? ADD_TO_CART_STATE.SUCCESS
          : ADD_TO_CART_STATE.ERROR
      );
    }).finally(() => setShowLoading(false));
  }

  return (
    <>
      <ProductHeader
        classes={{ container: styles.productHeader }}
        breadcrumbs={breadcrumb}
        productAttributes={productAttributes}
      />
      <div className={styles.container}>
        <div className={styles.left}>
          {productAttributes.imgFacePivot && (
            <div className={styles.picture}>
              <Image
                src={apiUrls.getPictureUrl(
                  productAttributes.imgFacePivot.Value
                )}
                alt={productName || 'Product image'}
              />
            </div>
          )}
          {productName && (
            <h1 className={styles.productTitle}>{productName}</h1>
          )}
          {cmsContent && (
            <InsertHtmlContent
              className={styles.cmsBlock}
              htmlContent={cmsContent}
            />
          )}
        </div>
        <div className={styles.right}>
          {productName && (
            <div className={styles.productTitle}>{productName}</div>
          )}
          {!showProducts && (
            <div className={styles.loadingProducts}>
              <Loading
                loading
                disablePortal
                disableBodyScrollbar={false}
                className={styles.loadingBackdrop}
              />
            </div>
          )}
          {canBuyAll && (
            <Button
              fullWidth
              model={'primary'}
              className={classNames(styles.addAllButton)}
              onClick={handleBuyAll}
            >
              {t('GROUPED_BUY_ALL')}
            </Button>
          )}
          <div
            className={classNames(
              styles.products,
              !showProducts && styles.hidden
            )}
          >
            {linkedProducts.map(({ id, url }) => (
              <ErrorBoundary key={id} fallback={<></>}>
                <ProductWrapper
                  path={url}
                  setIsReady={isReady => {
                    setProductsReady(prevState => ({
                      ...prevState,
                      [id]: isReady,
                    }));
                  }}
                  setProduct={product => {
                    setProducts(prevState => ({
                      ...prevState,
                      [id]: product,
                    }));
                  }}
                />
              </ErrorBoundary>
            ))}
          </div>
          <Tabs />
        </div>
      </div>
      {showLoading && <Loading loading />}
      {addToCartState !== ADD_TO_CART_STATE.NONE && (
        <AddToCartModal
          product={product as ProductType}
          state={addToCartState}
          updateState={state => setAddToCartState(state)}
          isAllProducts
        />
      )}
    </>
  );
});

export default GroupedProductView;
